import { yupResolver } from "@hookform/resolvers/yup";
import { AvatarCharecter, AvatarImage, ImageUploadWithPreview, InputSelect, SelectMulti, TextArea, TextAreaCus, TextField } from "components";
import { useApp, useMember, useProject } from "hooks";
import { SaveAll } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { defaultProjectValues, projectCreateSchema, projectStatusOptions, repareURL } from "utils";

interface IProjectForm {
    onClose?: () => void;
    onConfirm?: () => void;
    item?: any;
}

export default function ProjectForm({ onClose, item }: IProjectForm) {
    const router = useNavigate();
    const { onUpdateParams, projectIsFetch, dataTags } = useApp()

    const { register, control, setValue, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(projectCreateSchema),
        defaultValues: item ? {
            ...item,
            members: item?.members.map(el => ({ value: el.id, label: el.name, avatar: el.avatar })),
            tags: item?.tags.map(el => ({ value: el.id, label: el.name })),
        } : defaultProjectValues
    });
    const { createProj, updateProj } = useProject();

    const { fetchDataSearch } = useMember();
    const { data: dataMembers } = fetchDataSearch();
    const [options, setOptions] = useState([]);
    const [valSelected, setValSelected] = useState([]);
    // dataTags
    const [optionTags, setOptionTags] = useState([]);
    const [valTagsSelected, setValTagsSelected] = useState([]);

    useEffect(() => {
        if (item) {
            setValue('avatar', null);
            const mems = item?.members.map(el => ({ value: el.id, label: el.name, avatar: el.avatar })).filter(e => e)
            setValSelected(mems)
        }
    }, [item]);

    useEffect(() => {
        if (dataTags.length) {
            setOptionTags(dataTags.map(el => ({ value: el.id, label: el.name })))
        }
    }, [dataTags]);

    useEffect(() => {
        if (dataMembers?.data) {
            setOptions(dataMembers.data.map(el => ({ value: el.id, label: el.name, avatar: el.avatar })))
        }
    }, [dataMembers]);

    const onRedirect = () => {
        onUpdateParams({ projectIsFetch: !projectIsFetch })
        router(-1);
    }

    const onSubmit = (data) => {
        if (item) {
            updateProj.mutate({ ...data, members: valSelected.map(e => +e.value), tags: valTagsSelected.map(e => +e.value) }, {
                onSuccess: (res) => {
                    if (res.statusCode === 200) {
                        onUpdateParams({ projectDetail: res.data })
                    }
                    onClose();
                },
            })
            return;
        }
        createProj.mutate({ ...data, members: valSelected.map(e => +e.value), tags: valTagsSelected.map(e => +e.value) }, {
            onSuccess: (res) => {
                onRedirect();
            },
        })
    }

    return (
        <div className="mx-auto">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white rounded-lg shadow-sm p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-1">
                            <div>
                                <label className="block font-medium mb-1">Tên dự án</label>
                                <TextField
                                    type="text"
                                    placeholder="Nhập tên dự án"
                                    {...register('name')}
                                />
                            </div>

                        </div>

                        <div>
                            <label className="block font-medium mb-1">Mô tả:</label>
                            <Controller
                                control={control}
                                name="description"
                                render={({ field }) => (
                                    <TextArea
                                        {...field}
                                        placeholder="Nhập mô tả ngắn..."
                                        {...register("description")}
                                        rows={3}
                                    />
                                )}
                            />
                        </div>

                        <div>
                            <label className="block font-medium mb-1">Nội dung:</label>
                            <Controller
                                control={control}
                                name="content"
                                render={({ field }) => (
                                    <TextAreaCus
                                        {...field}
                                        className="h-300"
                                        style={{ height: 300, paddingBottom: 50 }}
                                        placeholder="Nhập nội dung..."
                                    />

                                )}
                            />
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            <div className="">
                                <label className="block font-medium mb-1">Ngày bắt đầu</label>
                                <TextField type="date" {...register("start_date")} />
                            </div>
                            <div className="">
                                <label className="block font-medium mb-1">Ngày đến hạn</label>
                                <TextField type="date" {...register("due_date")} />
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Trạng thái</label>
                                <Controller
                                    control={control}
                                    name="status"
                                    render={({ field }) => (
                                        <InputSelect options={projectStatusOptions} {...field} />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="font-medium mb-2">Thành viên</div>
                            <Controller
                                control={control}
                                name="members"
                                render={({ field }) => (
                                    <SelectMulti
                                        options={options}
                                        {...field}
                                        onChange={v => {
                                            setValSelected(v);
                                            field.onChange(v)
                                        }}
                                    />
                                )}
                            />

                            <div className="flex space-x-1 py-3">
                                {valSelected.map((e, i) => (
                                    e.avatar ? <AvatarImage
                                        key={i}
                                        alt={e.label}
                                        src={repareURL(e.avatar)}
                                        className="w-12 h-12 rounded-full border-2 border-white shadow p-1"
                                    /> : <AvatarCharecter className="w-12 h-12" name={e.label} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                        {item?.avatar && <div>
                            <label className="block font-medium mb-1">Banner</label>
                            <img src={repareURL(item?.avatar)} alt={item?.name} />
                        </div>}
                        <div>
                            <label className="block font-medium mb-1">Hình biểu tượng</label>
                            <p className="text-sm text-gray-500 mb-2">Kích thước đề xuất 800x400 (px).</p>
                            <Controller
                                control={control}
                                name="avatar"
                                render={({ field }) => (
                                    <ImageUploadWithPreview {...field} onPick={f => field.onChange(f)} />
                                )}
                            />
                        </div>
                        {!!optionTags.length && <div className="mt-6">
                            <div className="font-medium mb-2">Thẻ</div>
                            <Controller
                                control={control}
                                name="tags"
                                render={({ field }) => (
                                    <SelectMulti
                                        options={optionTags}
                                        {...field}
                                        onChange={v => {
                                            setValTagsSelected(v)
                                            field.onChange(v)
                                        }}
                                    />
                                )}
                            />
                        </div>}
                        <button
                            type="submit"
                            className={`px-4 py-4 w-full rounded-lg text-sm font-medium transition-colors bg-green-100 text-green-700 hover:bg-green-600 hover:text-white`}
                        >
                            <div className="flex justify-center"> <SaveAll className="mr-2" /> Lưu</div>
                        </button>
                    </div>
                </div>
            </form>

        </div>
    );
}
